import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { useAuthStore } from '@/lib/auth/authStore';
import { queryClient } from '@/lib/api/queryClient';
import { toast } from '@/components/ui/Toast';

/**
 * Caso 10 v2 — subscribe to the realtime `/event-monitor` channel for a
 * single Xotski event. On every `monitor:tick` we invalidate the
 * `liveMonitor` query — the page already knows how to refetch and re-render.
 *
 * Polling on the page stays as the warm fallback: even if the socket
 * fails to connect (e.g. behind a proxy without sticky sessions), the
 * 5-second refetchInterval keeps the dashboard accurate.
 *
 * Returns `connected` so the UI can show a "live" vs "polling" indicator.
 */
export function useEventMonitorSocket(eventId: number, queryKey: unknown[]) {
  const token = useAuthStore((s) => s.token);
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!token || !Number.isFinite(eventId) || eventId <= 0) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setConnected(false);
      return;
    }

    const apiBase = import.meta.env.VITE_API_URL ?? '';
    const host = apiBase.replace(/\/api\/v1\/?$/, '');
    const namespace = `${host}/event-monitor`;

    const socket = io(namespace, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    const handleConnect = () => {
      setConnected(true);
      socket.emit('subscribe', { eventId });
    };
    const handleDisconnect = () => setConnected(false);
    const handleTick = () => {
      queryClient.invalidateQueries({ queryKey });
    };
    // Caso 10 v3 — threshold alerts. The BE dedupes; here we just surface
    // each one as a toast. Quiet failure if the payload is malformed.
    const handleAlert = (payload: {
      kind?: string;
      remaining?: number;
      threshold?: number;
      metric?: string;
    }) => {
      if (!payload?.kind) return;
      if (payload.kind === 'low-stock') {
        toast({
          tone: 'error',
          title: 'Low stock',
          description: `Only ${payload.remaining ?? 0} prize${
            payload.remaining === 1 ? '' : 's'
          } left at this event.`,
        });
        return;
      }
      if (payload.kind === 'goal-met') {
        toast({
          tone: 'success',
          title: 'Goal reached',
          description: `Target hit on ${payload.metric ?? '—'}.`,
        });
        return;
      }
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('monitor:tick', handleTick);
    socket.on('monitor:alert', handleAlert);

    return () => {
      socket.emit('unsubscribe', { eventId });
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('monitor:tick', handleTick);
      socket.off('monitor:alert', handleAlert);
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
    // queryKey is intentionally a value dependency — useMemo at the call
    // site keeps the reference stable.
  }, [token, eventId, queryKey]);

  return { connected };
}
